import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardList, Clock, CheckCircle2, XCircle, Plus, Download, FileSpreadsheet, BarChart3, Search, CalendarIcon, CheckSquare, Trash2, RotateCcw, Eye, EyeOff, Home } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AgencySummary } from "@/components/AgencySummary";
import { OutstandingSurveys } from "@/components/OutstandingSurveys";
import { RecentBookingChanges } from "@/components/RecentBookingChanges";
import { ActionCenter } from "@/components/ActionCenter";
import { ShieldCheck } from "lucide-react";

const STATUS_COLORS = {
  pending_department: "bg-amber-100 text-amber-800 border-amber-200",
  pending_cmo: "bg-blue-100 text-blue-800 border-blue-200",
  pending_housing: "bg-purple-100 text-purple-800 border-purple-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABELS = {
  pending_department: "Dept Review",
  pending_cmo: "CMO Review",
  pending_housing: "Housing",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

const GUEST_STATUS_STYLES = {
  pending:    "bg-amber-100 text-amber-900 border-amber-200",
  pending_department: "bg-purple-100 text-purple-900 border-purple-200",
  pending_cmo:        "bg-indigo-100 text-indigo-900 border-indigo-200",
  needs_info: "bg-blue-100 text-blue-900 border-blue-200",
  approved:   "bg-emerald-100 text-emerald-900 border-emerald-200",
  booked:     "bg-[#E9F5DB] text-[#1B4332] border-[#1B4332]/20",
  declined:   "bg-red-100 text-red-900 border-red-200",
};
const GUEST_STATUS_LABELS = {
  pending: "Pending review",
  pending_department: "Awaiting dept head",
  pending_cmo: "Awaiting CMO",
  needs_info: "Needs info",
  approved: "Approved",
  booked: "Booked",
  declined: "Declined",
};

const CHART_COLORS = {
  pending_department: "#F59E0B",
  pending_cmo: "#3B82F6",
  pending_housing: "#8B5CF6",
  approved: "#10B981",
  rejected: "#EF4444",
  cancelled: "#9CA3AF",
};

const DEPT_COLORS = ["#1B4332", "#2D6A4F", "#40916C", "#52B788", "#74C69D", "#95D5B2", "#B7E4C7"];

const ROLE_LABELS = {
  admin: "Administrator",
  hospitality_coordinator: "Hospitality Coordinator",
  sending_agency: "Sending Agency",
  department_head: "Department Head",
  cmo_delegate: "CMO / Delegate",
};

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [guestRequests, setGuestRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requests");

  const canManageDeleted = hasRole("admin", "global_admin", "cmo_delegate", "hospitality_coordinator");
  const isAdmin = hasRole("admin", "global_admin");
  const approvalDepts = (user.approval_dept_keys && user.approval_dept_keys.length)
    ? user.approval_dept_keys : (user.department ? [user.department] : []);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      if (search) params.append("search", search);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (showDeleted && canManageDeleted) params.append("show_deleted", "true");
      const [statsRes, reqRes, analyticsRes, guestRes] = await Promise.all([
        api.get("/stats"),
        api.get(`/requests?${params.toString()}`),
        api.get("/analytics"),
        api.get("/guest-requests").catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setRequests(reqRes.data);
      setAnalytics(analyticsRes.data);
      setGuestRequests(Array.isArray(guestRes.data) ? guestRes.data : []);
      setSelectedIds([]);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  }, [filter, search, dateFrom, dateTo, showDeleted, canManageDeleted]);

  useEffect(() => {
    const debounce = setTimeout(fetchData, 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const filteredRequests = requests;

  // Merge non-medical guest requests into the same table. Server-side filters
  // only apply to medical requests, so mirror them client-side for guests.
  const GUEST_FILTER_MAP = { approved: ["approved", "booked"], rejected: ["declined"] };
  const filteredGuests = guestRequests.filter((g) => {
    if (filter !== "all") {
      const allowed = GUEST_FILTER_MAP[filter];
      if (!allowed || !allowed.includes(g.status)) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!`${g.guest_name} ${g.purpose || ""} ${g.submitted_by_name || ""}`.toLowerCase().includes(q)) return false;
    }
    if (dateFrom && (g.arrival_date || "") < dateFrom) return false;
    if (dateTo && (g.arrival_date || "") > dateTo) return false;
    return true;
  });
  const combinedRows = [
    ...(typeFilter === "guest" ? [] : filteredRequests.map((r) => ({ kind: "medical", row: r, created: r.created_at || "" }))),
    ...(typeFilter === "medical" ? [] : filteredGuests.map((g) => ({ kind: "guest", row: g, created: g.created_at || "" }))),
  ].sort((a, b) => (b.created || "").localeCompare(a.created || ""));

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const eligible = filteredRequests
      .filter((r) => canBulkAction(r) || canAdminSelect(r))
      .map((r) => r.id);
    if (selectedIds.length === eligible.length && eligible.length > 0) setSelectedIds([]);
    else setSelectedIds(eligible);
  };

  const canBulkAction = (r) => {
    if (hasRole("department_head") && r.status === "pending_department" && approvalDepts.includes(r.department)) return true;
    if (hasRole("cmo_delegate") && (r.status === "pending_cmo" || r.status === "pending_department")) return true;
    return false;
  };

  // Admin / global-admin can select ANY request in view for soft-delete / restore.
  const canAdminSelect = (r) => isAdmin;

  const canSelectRow = (r) => canBulkAction(r) || canAdminSelect(r);

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/requests/bulk-action", { request_ids: selectedIds, action });
      toast.success(`Bulk ${action}: ${data.success} succeeded, ${data.failed} failed`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    } finally { setBulkLoading(false); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Hide ${selectedIds.length} request(s) from the public view? They'll be kept for audit and can be restored.`)) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/requests/bulk-soft-delete", { request_ids: selectedIds });
      toast.success(`Hidden ${data.deleted} request(s) from public view`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setBulkLoading(false); }
  };

  const handleRestore = async (id) => {
    try {
      await api.post(`/requests/${id}/restore`);
      toast.success("Request restored");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    }
  };

  const showApproveBar = hasRole("department_head", "cmo_delegate") && selectedIds.length > 0;
  const showAdminDeleteBar = isAdmin && selectedIds.length > 0;

  const handleExport = async () => {
    try {
      const response = await api.get("/export/requests", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "visitor_requests.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel file downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-6 h-6 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statusChartData = analytics?.by_status?.map(s => ({
    name: STATUS_LABELS[s.name] || s.name,
    value: s.count,
    fill: CHART_COLORS[s.name] || "#9CA3AF"
  })) || [];

  const deptChartData = analytics?.by_department?.map((d, i) => ({
    name: d.name,
    count: d.count,
    fill: DEPT_COLORS[i % DEPT_COLORS.length]
  })) || [];

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-[#525C58] mt-1">
            Welcome back, {user.name}
            {user.department && <span className="text-[#7A8A84]"> &middot; {user.department}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-[#1B4332] text-white border-0 px-3 py-1" data-testid="role-badge">
            {ROLE_LABELS[user.role] || user.role}
          </Badge>
          {(hasRole("hospitality_coordinator", "sending_agency") || user.is_agency) && (
            <Button
              onClick={() => navigate("/requests/new")}
              className="bg-[#1B4332] hover:bg-[#2D6A4F] transition-colors duration-200"
              data-testid="new-request-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> New Request
            </Button>
          )}
          <Button variant="outline" onClick={handleExport} data-testid="export-btn" className="border-[#E8E8E6] hover:bg-[#F4F4F1]">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-cards">
          <StatCard icon={ClipboardList} label="Total Requests" value={stats.total} color="text-[#1B4332]" />
          <StatCard icon={Clock} label="Pending Review" value={stats.pending_department + stats.pending_cmo} color="text-amber-600" />
          <StatCard icon={CheckCircle2} label="Approved" value={stats.approved} color="text-emerald-600" />
          <StatCard icon={XCircle} label="Rejected" value={stats.rejected} color="text-red-500" />
        </div>
      )}

      {/* Phase 66 — agency at-a-glance: arrivals, on-site, documents needed */}
      {(user.role === "sending_agency" || user.is_agency) && <AgencySummary />}

      {/* Phase 73 — hospitality action center: today / this week / hints */}
      <ActionCenter />

      {/* Phase 67 — surveys the signed-in user owns and must close out */}
      <OutstandingSurveys />

      {/* Phase 69 — recent booking revisions + hospitality acknowledgement */}
      <RecentBookingChanges />

      {/* Phase 66 — staff heads-up when license packages are incomplete */}
      {stats && (stats.licenses_waiting_docs ?? 0) > 0 && (
        <div
          onClick={() => navigate("/medical-licenses")}
          className="cursor-pointer rounded-lg border border-amber-300 bg-amber-50/60 p-5 hover:shadow-sm transition-shadow flex items-center gap-4"
          data-testid="licenses-waiting-docs-card"
        >
          <ShieldCheck className="h-8 w-8 text-amber-600" />
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-800">Licenses waiting on documents</div>
            <div className="text-2xl font-semibold text-[#1C1F1E]">{stats.licenses_waiting_docs}</div>
          </div>
          <span className="ml-auto text-xs text-amber-800">Open Medical Licenses →</span>
        </div>
      )}

      {/* Phase 60.2 — non-medical (guest) counts. Kept on their own row so the
          existing 4-up medical stats layout is untouched. Clicking any tile
          jumps to the Guest Requests page. */}
      {stats && (stats.guests_total ?? 0) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="guest-stats-cards">
          <div
            onClick={() => navigate("/guest-requests")}
            className="cursor-pointer rounded-lg border border-[#E8E8E6] bg-white p-5 hover:shadow-sm transition-shadow flex items-center gap-4"
            data-testid="guest-stat-total"
          >
            <Home className="h-8 w-8 text-stone-700" />
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7A8A84]">Non-Medical Guests</div>
              <div className="text-2xl font-semibold text-[#1C1F1E]">{stats.guests_total}</div>
            </div>
          </div>
          <div
            onClick={() => navigate("/guest-requests?status=pending")}
            className="cursor-pointer rounded-lg border border-[#E8E8E6] bg-white p-5 hover:shadow-sm transition-shadow flex items-center gap-4"
            data-testid="guest-stat-pending"
          >
            <Clock className="h-8 w-8 text-amber-600" />
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7A8A84]">Guests Pending</div>
              <div className="text-2xl font-semibold text-[#1C1F1E]">{stats.guests_pending ?? 0}</div>
            </div>
          </div>
          <div
            onClick={() => navigate("/guest-requests?status=approved")}
            className="cursor-pointer rounded-lg border border-[#E8E8E6] bg-white p-5 hover:shadow-sm transition-shadow flex items-center gap-4"
            data-testid="guest-stat-approved"
          >
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7A8A84]">Guests Approved</div>
              <div className="text-2xl font-semibold text-[#1C1F1E]">{stats.guests_approved ?? 0}</div>
            </div>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F4F4F1] border border-[#E8E8E6]">
          <TabsTrigger value="requests" className="data-[state=active]:bg-white" data-testid="tab-requests">
            <ClipboardList className="h-4 w-4 mr-2" /> Requests
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-white" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4 mr-2" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A8A84]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search visitors, specialties, departments..."
                  className="pl-9 border-[#E8E8E6]"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 border-[#E8E8E6] text-sm" data-testid="date-from-input" />
                <span className="text-[#7A8A84] text-sm">to</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 border-[#E8E8E6] text-sm" data-testid="date-to-input" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40 border-[#E8E8E6]" data-testid="type-filter">
                  <SelectValue placeholder="Visitor type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Visitors</SelectItem>
                  <SelectItem value="medical">Medical</SelectItem>
                  <SelectItem value="guest">Non-Medical</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-44 border-[#E8E8E6]" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending_department">Dept Review</SelectItem>
                  <SelectItem value="pending_cmo">CMO Review</SelectItem>
                  <SelectItem value="pending_housing">Housing</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canManageDeleted && (
              <div className="flex items-center gap-3 p-3 bg-[#F4F4F1] rounded-lg border border-[#E8E8E6]" data-testid="show-deleted-wrap">
                {showDeleted ? <Eye className="h-4 w-4 text-[#7A8A84]" /> : <EyeOff className="h-4 w-4 text-[#7A8A84]" />}
                <Label htmlFor="show-deleted-toggle" className="text-sm text-[#1C1F1E] cursor-pointer select-none flex-1">
                  {showDeleted ? "Showing deleted requests" : "Hidden deleted requests from view"}
                </Label>
                <Switch id="show-deleted-toggle" checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="show-deleted-toggle" />
              </div>
            )}

            {showApproveBar && (
              <div className="flex items-center gap-3 p-3 bg-[#F4F4F1] rounded-lg border border-[#E8E8E6]" data-testid="bulk-actions-bar">
                <CheckSquare className="h-4 w-4 text-[#1B4332]" />
                <span className="text-sm text-[#1C1F1E] font-medium">{selectedIds.length} selected</span>
                <Button size="sm" onClick={() => handleBulkAction("approve")} className="bg-emerald-600 hover:bg-emerald-700 h-8" disabled={bulkLoading} data-testid="bulk-approve-btn">
                  Approve All
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleBulkAction("decline")} className="h-8" disabled={bulkLoading} data-testid="bulk-decline-btn">
                  Decline All
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="h-8">
                  Clear
                </Button>
              </div>
            )}

            {showAdminDeleteBar && (
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200" data-testid="admin-delete-bar">
                <Trash2 className="h-4 w-4 text-red-700" />
                <span className="text-sm text-red-900 font-medium">{selectedIds.length} selected</span>
                <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="h-8" disabled={bulkLoading} data-testid="bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Hide from public view
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="h-8 text-red-700 hover:bg-red-100">
                  Clear
                </Button>
              </div>
            )}

            <Card className="border-[#E8E8E6]" data-testid="requests-table">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[#7A8A84] font-medium w-10">
                  {(hasRole("department_head", "cmo_delegate") || isAdmin) && (
                    <Checkbox checked={selectedIds.length > 0 && selectedIds.length === filteredRequests.filter(r => canSelectRow(r)).length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                  )}
                </TableHead>
                <TableHead className="text-[#7A8A84] font-medium">Visitor</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Type</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Department</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Dates</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Party</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Submitted by</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Status</TableHead>
                    <TableHead className="text-[#7A8A84] font-medium">Created</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {combinedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-[#7A8A84] py-12">
                        <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        No requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    combinedRows.map(({ kind, row }) => kind === "guest" ? (
                      <TableRow
                        key={`guest-${row.id}`}
                        className="cursor-pointer hover:bg-[#F4F4F1] transition-colors duration-150"
                        onClick={() => navigate(`/guest-requests/${row.id}`)}
                        data-testid={`guest-row-${row.id}`}
                      >
                        <TableCell className="w-10" />
                        <TableCell className="font-medium text-[#1C1F1E]">{row.guest_name}</TableCell>
                        <TableCell>
                          <Badge className="bg-stone-100 text-stone-700 border-stone-200 border text-[10px]" data-testid={`type-badge-guest-${row.id}`}>
                            <Home className="h-3 w-3 mr-1" /> Guest
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#525C58] text-sm">{row.purpose || "Guest stay"}</TableCell>
                        <TableCell className="text-[#525C58] text-sm">{row.arrival_date} &rarr; {row.departure_date}</TableCell>
                        <TableCell className="text-[#525C58]">{row.num_guests || 1}</TableCell>
                        <TableCell className="text-[#525C58] text-sm">{row.submitted_by_name || "—"}</TableCell>
                        <TableCell>
                          <Badge className={`${GUEST_STATUS_STYLES[row.status] || "bg-gray-100 text-gray-600"} border text-xs`}>
                            {GUEST_STATUS_LABELS[row.status] || row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#7A8A84] text-sm">
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="text-[#40916C] hover:text-[#1B4332]" onClick={() => navigate(`/guest-requests/${row.id}`)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : (() => { const req = row; return (
                      <TableRow
                        key={req.id}
                        className={`cursor-pointer hover:bg-[#F4F4F1] transition-colors duration-150 ${req.deleted ? "opacity-60 bg-red-50/40" : ""}`}
                        onClick={() => navigate(`/requests/${req.id}`)}
                        data-testid={`request-row-${req.id}`}
                      >
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          {canSelectRow(req) && (
                            <Checkbox checked={selectedIds.includes(req.id)} onCheckedChange={() => toggleSelect(req.id)} data-testid={`select-${req.id}`} />
                          )}
                        </TableCell>
                        <TableCell className="font-medium text-[#1C1F1E]">
                          {req.visitor_name}
                          {req.deleted && (
                            <Badge className="ml-2 bg-red-100 text-red-800 border-red-200 border text-[10px]" data-testid={`deleted-badge-${req.id}`}>Archived</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-[#E9F5DB] text-[#1B4332] border-[#1B4332]/20 border text-[10px]" data-testid={`type-badge-medical-${req.id}`}>
                            <ShieldCheck className="h-3 w-3 mr-1" /> Medical
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#525C58]">{req.department}</TableCell>
                        <TableCell className="text-[#525C58] text-sm">{req.arrival_date} &rarr; {req.departure_date}</TableCell>
                        <TableCell className="text-[#525C58]">{req.party_size}</TableCell>
                        <TableCell className="text-[#525C58] text-sm" data-testid={`submitted-by-${req.id}`}>
                          {req.created_by_name || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[req.status] || "bg-gray-100 text-gray-600"} border text-xs`}>
                            {STATUS_LABELS[req.status] || req.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#7A8A84] text-sm">
                          {new Date(req.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {req.deleted && isAdmin ? (
                            <Button variant="ghost" size="sm" className="text-amber-700 hover:text-amber-900" onClick={() => handleRestore(req.id)} data-testid={`restore-${req.id}`}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="text-[#40916C] hover:text-[#1B4332]" onClick={() => navigate(`/requests/${req.id}`)}>
                              View
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ); })())
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="analytics-section">
            <Card className="border-[#E8E8E6]">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Requests by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {statusChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-[#7A8A84] text-center py-12">No data yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#E8E8E6]">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Requests by Department</CardTitle>
              </CardHeader>
              <CardContent>
                {deptChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={deptChartData} layout="vertical" margin={{ left: 20 }}>
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12, fill: "#525C58" }} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {deptChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-[#7A8A84] text-center py-12">No data yet</p>
                )}
              </CardContent>
            </Card>

            {analytics?.monthly_trends?.length > 0 && (
              <Card className="border-[#E8E8E6] lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Monthly Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={analytics.monthly_trends}>
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#525C58" }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total" name="Total" fill="#1B4332" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="approved" name="Approved" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="rejected" name="Rejected" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="border-[#E8E8E6] hover:shadow-sm transition-shadow duration-200">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium text-[#7A8A84]">{label}</CardTitle>
        <Icon className={`h-5 w-5 ${color} opacity-70`} />
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-semibold ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
